import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useProfileEnhancement } from './hooks/useProfileEnhancement';
import DocumentViewer from '../../components/viewers/DocumentViewer';
import MarkdownEditor from '../../components/editor/MarkdownEditor';
import { pdfCacheService } from './services/pdfCacheService';
import ProfileManager from './components/ProfileManager';
import SharedSidebar from '../../components/layout/SharedSidebar';
import { generateProfileFilename, extractNameFromMarkdown, getFallbackFilename } from '../../utils/fileNaming';
import { useAuth } from '../../contexts/AuthContext';
import { EntraService } from './services/entraService';
import { useMsal } from '@azure/msal-react';
import { SharePointSaveButton } from './components/SharePointSaveButton';
import './Enhancer.css';

const Enhancer: React.FC = () => {
  const documentProcessing = useProfileEnhancement();
  const { user } = useAuth();
  const { instance } = useMsal();
  const entraServiceRef = useRef<EntraService | null>(null);
  const [isSidebarOpen, setSidebarOpen] = useState(true);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [hasUploadedFile, setHasUploadedFile] = useState(false);
  // Removed currentStep - no longer needed for two-panel layout
  const [hasExportedPDF, setHasExportedPDF] = useState(false);
  // Removed uploaded file states - no longer needed for streamlined workflow
  const [markdownContent, setMarkdownContent] = useState<string>('');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [generatedPdfFile, setGeneratedPdfFile] = useState<File | null>(null);
  const [generatedPdfUrl, setGeneratedPdfUrl] = useState<string | null>(null);
  const [profilePicture, setProfilePicture] = useState<string | null>(null);
  const [, setCurrentMarkdownHash] = useState<string | null>(null);
  const [, setCurrentDocumentId] = useState<string | null>(null);

  const sharePointFilename = useMemo(() => {
    const { firstName, lastName } = extractNameFromMarkdown(markdownContent);
    if (firstName || lastName) {
      return generateProfileFilename({ firstName, lastName, format: 'pdf' });
    }
    return getFallbackFilename('profile', 'pdf');
  }, [markdownContent]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (generatedPdfUrl) {
        URL.revokeObjectURL(generatedPdfUrl);
      }
    };
  }, [generatedPdfUrl]);

  // Initialize EntraService and PDF cache
  useEffect(() => {
    if (instance) {
      entraServiceRef.current = new EntraService(instance);
    }
  }, [instance]);

  // Initialize PDF cache and load profile from Azure
  useEffect(() => {
    const initAndLoad = async () => {
      if (!user?.localAccountId || !entraServiceRef.current) return;
      
      try {
        // Initialize PDF cache
        await pdfCacheService.init();
        
        // Load profile from Azure Blob Storage
        setIsLoadingProfile(true);
        await loadProfileFromAzure();
      } catch (error) {
        console.error('Failed to initialize and load profile:', error);
      } finally {
        setIsLoadingProfile(false);
      }
    };
    
    initAndLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // Only depend on user to avoid infinite loops

  const loadProfileFromAzure = async () => {
    if (!entraServiceRef.current || !user?.localAccountId) return;
    
    try {
      // Load markdown from Azure Blob Storage
      const profileData = await entraServiceRef.current.getProfile();
      
      if (profileData && profileData.profile_markdown) {
        console.log('Profile loaded from Azure:', profileData.original_length, 'chars');
        setMarkdownContent(profileData.profile_markdown);
        
        // Calculate hash and check for cached PDF
        const hash = await pdfCacheService.calculateHash(profileData.profile_markdown);
        setCurrentMarkdownHash(hash);
        
        // Try to load cached PDF if hash matches
        const cachedPdf = await pdfCacheService.getCachedPDF(user.localAccountId, hash);
        if (cachedPdf) {
          console.log('Found cached PDF for hash:', hash.substring(0, 8) + '...');
          
          // Clean up any existing URL first
          if (generatedPdfUrl) {
            URL.revokeObjectURL(generatedPdfUrl);
          }
          
          // Create new PDF file and URL from cache
          // Generate standardized filename from markdown content
          const { firstName, lastName } = extractNameFromMarkdown(markdownContent);
          const filename = firstName || lastName
            ? generateProfileFilename({ firstName, lastName, format: 'pdf' })
            : getFallbackFilename('profile', 'pdf');
          const pdfFile = new File([cachedPdf], filename, { 
            type: 'application/pdf' 
          });
          setGeneratedPdfFile(pdfFile);
          
          const pdfUrl = URL.createObjectURL(cachedPdf);
          setGeneratedPdfUrl(pdfUrl);
          
          // Important: Set hasExportedPDF to true to show the PDF panel
          setHasExportedPDF(true);
          
          console.log('Loaded cached PDF for matching markdown');
        } else {
          // Clear PDF if no valid cache
          if (generatedPdfUrl) {
            URL.revokeObjectURL(generatedPdfUrl);
          }
          setGeneratedPdfUrl(null);
          setGeneratedPdfFile(null);
          setHasExportedPDF(false);
          console.log('No valid PDF cache found for hash:', hash.substring(0, 8) + '...');
        }
      } else {
        console.log('No profile found in Azure');
        setMarkdownContent('');
        setCurrentMarkdownHash(null);
      }
    } catch (error) {
      console.error('Failed to load profile from Azure:', error);
      // Silently fail - user might not have a profile yet
    }
  };

  const saveProfileToAzure = async (content: string) => {
    if (!entraServiceRef.current) return;
    
    setIsSavingProfile(true);
    try {
      await entraServiceRef.current.saveProfile(content);
      console.log('Profile saved to Azure');
      
      // Update the current markdown hash after saving
      const hash = await pdfCacheService.calculateHash(content);
      setCurrentMarkdownHash(hash);
      
      // Show success feedback (you might want to add a toast notification here)
    } catch (error) {
      console.error('Failed to save profile to Azure:', error);
      alert('Failed to save profile to Azure. Please try again.');
    } finally {
      setIsSavingProfile(false);
    }
  };
  
  const loadProfilePicture = async () => {
    if (!entraServiceRef.current) return;
    
    try {
      const pictureUrl = await entraServiceRef.current.getProfilePicture();
      if (pictureUrl) {
        setProfilePicture(pictureUrl);
        console.log('Profile picture loaded');
      }
    } catch (error) {
      console.warn('Could not load profile picture:', error);
    }
  };

  // Auto-close sidebar when file is uploaded
  useEffect(() => {
    if (documentProcessing.uploadedDocument && !hasUploadedFile) {
      setHasUploadedFile(true);
      setSidebarOpen(false);
      setCurrentDocumentId(documentProcessing.uploadedDocument.document_id);
      // Save to localStorage for persistence
      localStorage.setItem('lastOpenedDocument', documentProcessing.uploadedDocument.document_id);
      // Reset step to original when uploading new file
      // Clear markdown content to wait for processing result
      setMarkdownContent('');
    }
  }, [documentProcessing.uploadedDocument, hasUploadedFile]);

  // Keep sidebar open when no file uploaded
  useEffect(() => {
    if (!documentProcessing.uploadedDocument && hasUploadedFile) {
      setHasUploadedFile(false);
      setSidebarOpen(true);
    }
  }, [documentProcessing.uploadedDocument, hasUploadedFile]);

  // Update markdown content when processing completes and auto-save
  useEffect(() => {
    if (documentProcessing.processingResult?.redacted_content) {
      const content = documentProcessing.processingResult.redacted_content;
      setMarkdownContent(content);
      
      // Auto-save the enhanced content to Azure
      if (entraServiceRef.current) {
        saveProfileToAzure(content).then(() => {
          console.log('Profile auto-saved after processing');
        }).catch((error) => {
          console.error('Failed to auto-save after processing:', error);
        });
      }
    }
  }, [documentProcessing.processingResult]);

  // Handle PDF blob from DOCX conversion - simplified since we don't show original
  useEffect(() => {
    if (documentProcessing.pdfBlob) {
      console.log('PDF conversion completed');
    }
  }, [documentProcessing.pdfBlob]);

  const handleFileUpload = async (files: FileList) => {
    if (files.length > 0) {
      const file = files[0];
      
      // First reset the document processing state to clear any previous document
      documentProcessing.resetState();
      
      // Clean up previous URLs
      if (generatedPdfUrl) {
        URL.revokeObjectURL(generatedPdfUrl);
      }
      
      // Clear all state from previous document
      setMarkdownContent(''); // Clear markdown content
      setGeneratedPdfFile(null);
      setGeneratedPdfUrl(null);
      setHasExportedPDF(false);
      
      // Upload and start processing
      await documentProcessing.uploadFile(file);
      // File uploaded - clear any cached PDF as it's now outdated
      if (generatedPdfUrl) {
        URL.revokeObjectURL(generatedPdfUrl);
        setGeneratedPdfUrl(null);
        setGeneratedPdfFile(null);
      }
      
      // Reload profile picture if it was cleared but still exists
      if (!profilePicture && entraServiceRef.current) {
        await loadProfilePicture();
      }
    }
  };

  /* Removed handleDocumentSelect - no longer needed for single profile
  const handleDocumentSelect = async (document: any) => {
    try {
      console.log('Document selected:', document);
      
      // Save to localStorage for persistence across refreshes
      localStorage.setItem('lastOpenedDocument', document.documentId);
      
      // Load the document from IndexedDB
      setCurrentDocumentId(document.documentId);
      
      // Clear any existing state and URLs
      if (uploadedFileUrl) {
        URL.revokeObjectURL(uploadedFileUrl);
        setUploadedFileUrl(null);
      }
      if (generatedPdfUrl) {
        URL.revokeObjectURL(generatedPdfUrl);
        setGeneratedPdfUrl(null);
      }
      setUploadedFileType(null);
      setGeneratedPdfFile(null);
      setHasExportedPDF(false);
      
      // Check if we have a PDF blob (for converted DOCX files) or original file
      const pdfBlob = (document as any).pdfBlob;
      const fileToLoad = pdfBlob || document.originalFile;
      
      if (fileToLoad) {
        console.log('Loading file...', pdfBlob ? 'PDF blob' : 'original file');
        try {
          // Ensure we have a valid Blob
          const blob = fileToLoad instanceof Blob 
            ? fileToLoad 
            : new Blob([fileToLoad], { type: pdfBlob ? 'application/pdf' : document.fileType });
          
          // Determine the file name and type
          let fileName = document.fileName;
          let mimeType = document.fileType || 'application/pdf';
          let fileType: 'pdf' | 'docx' = 'pdf';
          
          if (pdfBlob) {
            // If we have a PDF blob, it means this was a converted DOCX
            fileName = fileName.replace(/\.(docx?|doc)$/i, '.pdf');
            mimeType = 'application/pdf';
            fileType = 'pdf';
          } else {
            // Detect file type from filename or mime type
            const fileNameLower = fileName.toLowerCase();
            if (fileNameLower.endsWith('.pdf') || mimeType?.includes('pdf')) {
              fileType = 'pdf';
            } else if (fileNameLower.endsWith('.docx') || fileNameLower.endsWith('.doc') || 
                       mimeType?.includes('word') || mimeType?.includes('document')) {
              fileType = 'docx';
            }
          }
          
          // Create a File object from the stored Blob
          const file = new File([blob], fileName, { 
            type: mimeType,
            lastModified: Date.now()
          });
          
          console.log('Created file object:', file, 'Type:', fileType);
          setUploadedFile(file);
          setUploadedFileType(fileType);
          
          // Create blob URL
          const url = URL.createObjectURL(file);
          setUploadedFileUrl(url);
          console.log('Created blob URL:', url);
          setHasUploadedFile(true);
        } catch (error) {
          console.error('Error creating file from blob:', error);
        }
      }
      
      // Load the markdown content (edited or original)
      if (document.editedMarkdown) {
        console.log('Loading edited markdown...');
        setMarkdownContent(document.editedMarkdown);
      } else if (document.originalMarkdown) {
        console.log('Loading original markdown...');
        setMarkdownContent(document.originalMarkdown);
      }
      
      // Load the exported PDF if it exists
      if (document.exportedPDF) {
        console.log('Loading exported PDF...', document.exportedPDF);
        try {
          // Ensure we have a valid Blob
          const blob = document.exportedPDF instanceof Blob 
            ? document.exportedPDF 
            : new Blob([document.exportedPDF], { type: 'application/pdf' });
          
          // Create a File object from the stored Blob
          // Generate standardized filename from markdown content
          const { firstName, lastName } = extractNameFromMarkdown(markdownContent);
          const filename = firstName || lastName
            ? generateProfileFilename({ firstName, lastName, format: 'pdf' })
            : getFallbackFilename('profile', 'pdf');
          const pdfFile = new File([blob], filename, { 
            type: 'application/pdf',
            lastModified: Date.now()
          });
          console.log('Created PDF file object:', pdfFile);
          setGeneratedPdfFile(pdfFile);
          const url = URL.createObjectURL(pdfFile);
          setGeneratedPdfUrl(url);
          console.log('Created PDF blob URL:', url);
          setHasExportedPDF(true);
        } catch (error) {
          console.error('Error creating PDF file from blob:', error);
        }
      }
      
      // Show the document in workspace
      // Need to reset document processing state to show the loaded document
      documentProcessing.resetState();
      
      // Close the sidebar
      setSidebarOpen(false);
      console.log('Document loaded successfully');
    } catch (error) {
      console.error('Failed to load document:', error);
      alert('Failed to load document. Please try again.');
    }
  }; */

  /* Removed handleDocumentDelete - no longer needed for single profile
  const handleDocumentDelete = async (document: any, event: React.MouseEvent) => {
    // Stop propagation to prevent document selection
    event.stopPropagation();
    
    if (window.confirm(`Are you sure you want to delete "${document.fileName}"? This action cannot be undone.`)) {
      try {
        await profileStorageService.deleteDocument(document.documentId);
        console.log('Document deleted:', document.documentId);
        
        // If the deleted document was currently open, clear the workspace
        if (currentDocumentId === document.documentId) {
          // Reset document processing state (clears uploadedDocument, processingResult, etc.)
          documentProcessing.resetState();
          
          // Clear local state
          setUploadedFile(null);
          setUploadedFileType(null);
          setMarkdownContent('');
          setGeneratedPdfFile(null);
          // Clean up URLs
          if (uploadedFileUrl) {
            URL.revokeObjectURL(uploadedFileUrl);
            setUploadedFileUrl(null);
          }
          if (generatedPdfUrl) {
            URL.revokeObjectURL(generatedPdfUrl);
            setGeneratedPdfUrl(null);
          }
          setHasExportedPDF(false);
          setCurrentDocumentId(null);
          setHasUploadedFile(false);
          // Clear from localStorage too
          localStorage.removeItem('lastOpenedDocument');
        }
        
        // Reload the document list
        await loadProfile();
      } catch (error) {
        console.error('Failed to delete document:', error);
        alert('Failed to delete document. Please try again.');
      }
    }
  }; */

  const toggleSidebar = () => {
    setSidebarOpen(!isSidebarOpen);
  };

  const handleProfileDelete = async () => {
    try {
      if (!user?.localAccountId) {
        alert('No user profile found');
        return;
      }

      // Clear all state
      setMarkdownContent('');
      setGeneratedPdfFile(null);
      setHasUploadedFile(false);
      setHasExportedPDF(false);
      setCurrentDocumentId(null);
      setProfilePicture(null);
      
      // Clean up URLs
      if (generatedPdfUrl) {
        URL.revokeObjectURL(generatedPdfUrl);
        setGeneratedPdfUrl(null);
      }
      
      // Reset document processing state
      documentProcessing.resetState();
      
      // Clear from localStorage
      localStorage.removeItem('lastOpenedDocument');
      
      // Clear from Entra/Azure
      if (entraServiceRef.current) {
        try {
          await entraServiceRef.current.deleteProfile();
        } catch (error) {
          console.error('Failed to delete profile from Azure:', error);
          // Don't fail the whole operation if Azure delete fails
        }
      }
      
      console.log('Profile deleted successfully');
    } catch (error) {
      console.error('Failed to delete profile:', error);
      alert('Failed to delete profile. Please try again.');
    }
  };


  return (
    <div className="profile-enhancer-container">
      {/* SharedSidebar with ProfileManager content */}
      <SharedSidebar
        isOpen={isSidebarOpen}
        onToggle={toggleSidebar}
        title="My Profile"
        subtitle="Upload and enhance your own professional profile"
      >
        <ProfileManager
          onFileUpload={handleFileUpload}
          onProfilePictureUpdate={async (pictureUrl) => {
            setProfilePicture(pictureUrl);
            // Profile picture is now handled via Azure/Entra
            console.log('Profile picture updated');
          }}
          onProfileDelete={handleProfileDelete}
        />
      </SharedSidebar>

      <div className="workspace">
        {(documentProcessing.uploadedDocument || hasUploadedFile || markdownContent) && (
          <>
            <div className="panels-container two-panel-layout">
          {/* Markdown Editor Panel (First Panel) */}
          <div className="panel editor-panel">
            <div className="panel-header">
              <div className="panel-title">
                <span>Markdown Editor</span>
              </div>
              <div className="panel-controls">
                <button 
                  className="control-btn" 
                  title="Save"
                  onClick={async () => {
                    if (markdownContent) {
                      try {
                        // Save to Azure Blob Storage only
                        await saveProfileToAzure(markdownContent);
                        console.log('Content saved successfully to Azure');
                      } catch (error) {
                        console.error('Failed to save content:', error);
                        alert('Failed to save content. Please try again.');
                      }
                    }
                  }}
                  disabled={isSavingProfile || !markdownContent}
                >
                  <span className="material-icons">{isSavingProfile ? 'hourglass_empty' : 'save'}</span>
                </button>
                <button 
                  className="control-btn" 
                  title="Reset to original"
                  onClick={async () => {
                    if (window.confirm('Are you sure you want to reset to the original enhanced content?')) {
                      // Reset to the last processing result
                      setMarkdownContent(documentProcessing.processingResult?.redacted_content || '');
                    }
                  }}
                >
                  <span className="material-icons">refresh</span>
                </button>
                <button 
                  className="export-btn" 
                  onClick={async () => {
                    setIsGeneratingPDF(true);
                    try {
                      // Get the current markdown content
                      const content = markdownContent || documentProcessing.processingResult?.redacted_content || '';
                      
                      // Auto-save to Azure before generating PDF
                      if (content && entraServiceRef.current) {
                        try {
                          await saveProfileToAzure(content);
                          console.log('Profile auto-saved before PDF export');
                        } catch (saveError) {
                          console.error('Failed to auto-save before PDF export:', saveError);
                          // Continue with PDF generation even if save fails
                        }
                      }
                      
                      // Use the already loaded profile photo
                      const profilePhoto = profilePicture;
                      const userName = user?.name || null;
                      
                      // Call the backend API to generate PDF
                      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:9110'}/api/pdf/markdown-to-pdf`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                          markdown_content: content,
                          style: 'profiler',
                          theme: 'athlete',
                          filename: 'profile.pdf',
                          profile_photo: profilePhoto, // Will be null if no photo available
                          user_name: userName
                        }),
                      });
                      
                      if (!response.ok) {
                        throw new Error('PDF generation failed');
                      }
                      
                      // Get the PDF blob
                      const blob = await response.blob();
                      
                      // Create a File object from the blob
                      // Generate standardized filename from markdown content
                      const { firstName, lastName } = extractNameFromMarkdown(markdownContent);
                      const filename = firstName || lastName
                        ? generateProfileFilename({ firstName, lastName, format: 'pdf' })
                        : getFallbackFilename('profile', 'pdf');
                      const pdfFile = new File([blob], filename, {
                        type: 'application/pdf',
                        lastModified: Date.now()
                      });
                      setGeneratedPdfFile(pdfFile);
                      const pdfUrl = URL.createObjectURL(pdfFile);
                      setGeneratedPdfUrl(pdfUrl);
                      
                      // Cache PDF with hash
                      if (user?.localAccountId) {
                        try {
                          const hash = await pdfCacheService.calculateHash(content);
                          await pdfCacheService.cachePDF(user.localAccountId, blob, hash);
                          setCurrentMarkdownHash(hash);
                          console.log('PDF cached with hash');
                        } catch (cacheError) {
                          console.error('Failed to cache PDF:', cacheError);
                        }
                      }
                      
                      setHasExportedPDF(true);
                      
                    } catch (error) {
                      console.error('Error generating PDF:', error);
                      alert('Failed to generate PDF. Please try again.');
                    } finally {
                      setIsGeneratingPDF(false);
                    }
                  }}
                  disabled={isGeneratingPDF || (!markdownContent && !documentProcessing.processingResult)}
                >
                  <span className="material-icons">picture_as_pdf</span>
                  {isGeneratingPDF ? 'Generating...' : 'Export PDF'}
                </button>
              </div>
            </div>
            <div className="panel-content" style={{ position: 'relative' }}>
              {isGeneratingPDF && (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center',
                  zIndex: 10,
                  background: 'rgba(255, 255, 255, 0.95)',
                  padding: '20px',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  <div className="spinner" style={{ marginBottom: '16px', margin: '0 auto' }}></div>
                  <p style={{ color: 'var(--stone-grey)' }}>Generating PDF...</p>
                  <small style={{ color: 'var(--stone-grey)', fontSize: '12px' }}>Using MD2PDF with consulting style</small>
                </div>
              )}
              {documentProcessing.isUploading && (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center',
                  zIndex: 10,
                  background: 'rgba(255, 255, 255, 0.95)',
                  padding: '20px',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  <div className="spinner" style={{ marginBottom: '16px' }}></div>
                  <p style={{ color: 'var(--stone-grey)' }}>Uploading document...</p>
                  <small style={{ color: 'var(--stone-grey)', fontSize: '12px' }}>Please wait</small>
                </div>
              )}
              {documentProcessing.isProcessing && !documentProcessing.processingResult && (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center',
                  zIndex: 10,
                  background: 'rgba(255, 255, 255, 0.95)',
                  padding: '20px',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  <div className="spinner" style={{ marginBottom: '16px' }}></div>
                  <p style={{ color: 'var(--stone-grey)' }}>Processing document with AI...</p>
                  <small style={{ color: 'var(--stone-grey)', fontSize: '12px' }}>This may take a moment</small>
                </div>
              )}
              <MarkdownEditor
                value={markdownContent || documentProcessing.processingResult?.redacted_content || '# Your Profile\n\nStart by uploading your CV or profile document to see the enhanced content here.'}
                onChange={setMarkdownContent}
                height={500}
                placeholder="Start editing your own profile..."
                readOnly={(!documentProcessing.processingResult && !markdownContent) || isLoadingProfile}
              />
            </div>
          </div>

          {/* Generated PDF Panel (Second Panel) */}
          <div className={`panel ${!hasExportedPDF ? 'hide' : ''}`}>
            <div className="panel-header">
              <div className="panel-title">
                <span>Generated PDF</span>
              </div>
              <div className="panel-controls">
                <button 
                  className="download-btn"
                  onClick={() => {
                    if (generatedPdfFile) {
                      const url = URL.createObjectURL(generatedPdfFile);
                      const link = document.createElement('a');
                      link.href = url;
                      // Generate standardized filename from markdown content
                      const { firstName, lastName } = extractNameFromMarkdown(markdownContent);
                      const filename = firstName || lastName
                        ? generateProfileFilename({ firstName, lastName, format: 'pdf' })
                        : getFallbackFilename('profile', 'pdf');
                      link.download = filename;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      URL.revokeObjectURL(url);
                    }
                  }}
                >
                  <span className="material-icons">download</span>
                  Download
                </button>
                <SharePointSaveButton
                  pdfFile={generatedPdfFile}
                  disabled={!generatedPdfFile || isGeneratingPDF}
                  profileUserId={user?.localAccountId}
                  preferredFilename={sharePointFilename}
                  onError={(message) => console.error('SharePoint save failed:', message)}
                  onSaveComplete={() => {
                    console.log('Profile saved to SharePoint');
                  }}
                />
              </div>
            </div>
            <div className="panel-content">
              {generatedPdfUrl ? (
                <DocumentViewer
                  key={generatedPdfUrl}
                  file={generatedPdfUrl}
                  fileType="pdf"
                  height={600}
                  showToolbar={true}
                  onError={(error) => console.error('PDF viewer error:', error)}
                />
              ) : (
                <div className="pdf-viewer hexagon-pattern">
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <p style={{ color: 'var(--stone-grey)' }}>No generated content yet</p>
                  </div>
                </div>
              )}
            </div>
          </div>
            </div>
          </>
        )}
        
        {!documentProcessing.uploadedDocument && !hasUploadedFile && !markdownContent && (
          <div className="hexagon-pattern" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '100%',
            flexDirection: 'column',
            textAlign: 'center',
            padding: '40px'
          }}>
            <span className="material-icons" style={{ 
              fontSize: '96px', 
              color: 'rgba(255,255,255,0.3)', 
              marginBottom: '24px' 
            }}>description</span>
            <h2 style={{ 
              color: 'rgba(255,255,255,0.8)', 
              fontSize: '24px', 
              fontWeight: '500', 
              marginBottom: '16px' 
            }}>Welcome to Profile Enhancer</h2>
            <p style={{ 
              color: 'rgba(255,255,255,0.6)', 
              fontSize: '16px',
              maxWidth: '400px',
              lineHeight: '1.6'
            }}>Select a profile from the sidebar or upload a file to begin the enhancement process</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Enhancer;
